import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Package, CheckCircle, TrendingUp, Archive, Wrench } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AssetDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assets/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6 flex items-center justify-center">
          <div className="text-lg">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Asset Management Dashboard</h1>
          <p className="text-gray-600 mt-2">Overview of company assets and allocations</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <Package className="w-8 h-8" style={{color: '#2b3380'}} />
              <span className="text-3xl font-bold" style={{color: '#2b3380'}}>{stats?.total_assets || 0}</span>
            </div>
            <p className="text-gray-600 text-sm">Total Assets</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <span className="text-3xl font-bold text-green-600">{stats?.available || 0}</span>
            </div>
            <p className="text-gray-600 text-sm">Available</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-blue-600" />
              <span className="text-3xl font-bold text-blue-600">{stats?.assigned || 0}</span>
            </div>
            <p className="text-gray-600 text-sm">Assigned</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <Wrench className="w-8 h-8 text-orange-600" />
              <span className="text-3xl font-bold text-orange-600">{stats?.under_repair || 0}</span>
            </div>
            <p className="text-gray-600 text-sm">Under Repair</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <Archive className="w-8 h-8 text-gray-600" />
              <span className="text-3xl font-bold text-gray-600">{stats?.retired || 0}</span>
            </div>
            <p className="text-gray-600 text-sm">Retired</p>
          </div>
        </div>

        {/* Asset Types & Categories */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Asset Types */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>Assets by Type</h2>
            {stats?.asset_types && stats.asset_types.length > 0 ? (
              <div className="space-y-3">
                {stats.asset_types.map((type, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-gray-700">{type._id || 'Unknown'}</span>
                    <span className="px-3 py-1 rounded-full text-sm font-medium text-white" style={{backgroundColor: '#2b3380'}}>
                      {type.count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No asset types available</p>
            )}
          </div>

          {/* Categories */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>Assets by Category</h2>
            {stats?.categories && stats.categories.length > 0 ? (
              <div className="space-y-3">
                {stats.categories.map((category, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-gray-700">{category._id || 'Unknown'}</span>
                    <span className="px-3 py-1 rounded-full text-sm font-medium text-white" style={{backgroundColor: '#2b3380'}}>
                      {category.count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No categories available</p>
            )}
          </div>
        </div>

        {/* Recent Allocations */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>Recent Allocations</h2>
          {stats?.recent_allocations && stats.recent_allocations.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{backgroundColor: '#2b3380'}}>
                  <tr>
                    <th className="px-4 py-3 text-left text-white">Asset</th>
                    <th className="px-4 py-3 text-left text-white">Employee</th>
                    <th className="px-4 py-3 text-left text-white">Allocation Date</th>
                    <th className="px-4 py-3 text-left text-white">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_allocations.map((allocation, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{allocation.asset_name}</td>
                      <td className="px-4 py-3">{allocation.employee_name}</td>
                      <td className="px-4 py-3">{new Date(allocation.allocation_date).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{allocation.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No recent allocations</p>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default AssetDashboard;